import { useState, useMemo, useCallback, useEffect } from 'react'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import AccountSelect from '../../components/AccountSelect.jsx'
import PageState from '../../components/PageState.jsx'
import CampaignFilter from '../../components/CampaignFilter.jsx'
import { useCollapseSidebarOnOverlap } from '../../hooks/useSidebarOverlap.js'

// Sub-páginas
import QuantitativePage from './Quantitative.jsx'
import RatesPage from './Rates.jsx'
import ValuesPage from './Values.jsx'
import ProjectionsPage from './Projections.jsx'

// Integração com API
import { getAccounts, getGoogleAdsMetrics, getSalesMetrics, getWhatsappTotal, syncToday } from '../../db/api.js'
import { useApiData } from '../../hooks/useApiData.js'
import AccessGate from '../../components/AccessGate.jsx'

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENTES AUXILIARES
// ═════════════════════════════════════════════════════════════════════════════

function LevelSelector({ levels, activeLevel, onChange, disabled }) {
  return (
    <div className="ga-level-selector">
      <span className="ga-level-label">Nível:</span>
      <div className="ga-level-options">
        {levels.map(level => (
          <button
            key={level.id}
            className={`ga-level-btn ${activeLevel === level.id ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => !disabled && onChange(level.id)}
            disabled={disabled}
          >
            {level.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function PageSelector({ activePage, onChange }) {
  const pages = [
    { id: 'quantitative', label: 'Quantitativa', icon: 'chart' },
    { id: 'rates', label: 'Taxas', icon: 'percent' },
    { id: 'values', label: 'Valores', icon: 'currency' },
    { id: 'goals', label: 'Metas', icon: 'target', disabled: true },
    { id: 'projections', label: 'Projeções', icon: 'trend_up' },
  ]

  return (
    <div className="ga-page-selector">
      {pages.map(page => (
        <button
          key={page.id}
          className={`ga-page-btn ${activePage === page.id ? 'active' : ''} ${page.disabled ? 'is-disabled' : ''}`}
          onClick={() => !page.disabled && onChange(page.id)}
          aria-disabled={page.disabled || undefined}
          title={page.disabled ? 'Indisponível no momento' : undefined}
        >
          <Icon n={page.icon} s={14} />
          {page.label}
        </button>
      ))}
    </div>
  )
}

function Breadcrumb({ items, onNavigate }) {
  return (
    <div className="ga-breadcrumb">
      {items.map((item, index) => (
        <span key={item.id} className="ga-breadcrumb-item">
          {index > 0 && <span className="ga-breadcrumb-separator">›</span>}
          {item.active ? (
            <span className="ga-breadcrumb-active">{item.label}</span>
          ) : (
            <button className="ga-breadcrumb-link" onClick={() => onNavigate(item)}>
              {item.label}
            </button>
          )}
        </span>
      ))}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// PERSISTÊNCIA DOS FILTROS POR NÍVEL
// ═════════════════════════════════════════════════════════════════════════════
// O filtro de nível (Campanhas/Grupos/Anúncios) precisa manter o estado ao
// alternar abas e sobreviver a remounts/route changes. Guardamos um mapa
// { campaign, adgroup, ad } em sessionStorage, escopado por cliente (IDs de um
// cliente não valem para outro). `null`/ausente = "todas selecionadas".

const LEVEL_FILTER_STORAGE_KEY = 'deebs:gads:levelFilters'

function levelFilterStorageKey(clientId) {
  return `${LEVEL_FILTER_STORAGE_KEY}:${clientId ?? 'all'}`
}

function readLevelFilters(clientId) {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(levelFilterStorageKey(clientId))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeLevelFilters(clientId, value) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(levelFilterStorageKey(clientId), JSON.stringify(value))
  } catch {
    /* sessionStorage indisponível (modo privado) — degrada para só-memória */
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════

export default function GoogleAds() {
  const { toast, activeClient, period } = useApp()

  // Recolhe a sidebar só se o calendário/conteúdo não couber com ela aberta.
  useCollapseSidebarOnOverlap(1100)
  const activeClientId = activeClient?.id ?? null

  // Período vem do contexto global (mesmo calendário/PeriodPicker da Dashboard).
  // Default = "Últimos 7 dias" (definido no AppContext). Trocar o período
  // refaz as queries automaticamente (sem botão "Aplicar").

  const { data: accountsData, loading: accLoading, error: accError, reload: reloadAcc } = useApiData(getAccounts, [], { initialData: [] })

  const { data: campaignsData, loading: campLoading, error: campError, reload: reloadCamp } = useApiData(
    () => getGoogleAdsMetrics('campaign', period.start, period.end),
    [period.start, period.end],
    { initialData: [] }
  )

  const { data: adGroupsData, loading: groupLoading, error: groupError, reload: reloadGroup } = useApiData(
    () => getGoogleAdsMetrics('adgroup', period.start, period.end),
    [period.start, period.end],
    { initialData: [] }
  )

  const { data: adsData, loading: adLoading, error: adError, reload: reloadAd } = useApiData(
    () => getGoogleAdsMetrics('ad', period.start, period.end),
    [period.start, period.end],
    { initialData: [] }
  )

  // Essa chamada traz TODO o funil do CRM
  const { data: crmData, loading: crmLoading, error: crmError, reload: reloadCrm } = useApiData(
    () => getSalesMetrics(period.start, period.end),
    [period.start, period.end],
    { initialData: [] }
  )

  // Total canônico de cliques no WhatsApp (mesma definição do DRE/Dashboard).
  // A soma por conta de /sales-metrics descarta cliques com gclid válido mas
  // sem ad_account_id atribuído; este número fechado bate com a Dashboard e
  // alimenta o card agregado (nível Contas, sem filtro de conta).
  const { data: whatsTotalData, reload: reloadWhats } = useApiData(
    () => getWhatsappTotal(period.start, period.end),
    [period.start, period.end],
    { initialData: { total: null } }
  )

  const isLoading = accLoading || campLoading || groupLoading || adLoading || crmLoading
  const hasError = accError || campError || groupError || adError || crmError
  const reloadAll = () => {
    reloadAcc(); reloadCamp(); reloadGroup(); reloadAd(); reloadCrm(); reloadWhats()
  }

  const [activeLevel, setActiveLevel] = useState('account')
  const [activePage, setActivePage] = useState('quantitative')

  const [selectedAccounts, setSelectedAccounts] = useState([])

  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)

  // Filtro multi-seleção por nível (Campanhas/Grupos/Anúncios), espelhando o
  // CampaignFilter da Dashboard. `null` = todas selecionadas. Estado local
  // (não usa o singleton global useCampaignFilter) para não contaminar o
  // filtro da Dashboard.
  //
  // Persistência por aba: mapa { campaign, adgroup, ad } mantido em estado +
  // sessionStorage. Trocar de aba NÃO reseta — ao voltar, restaura o último
  // estado. Inicializado da sessionStorage (sobrevive a remount/route change).
  const [levelFilters, setLevelFilters] = useState(() => readLevelFilters(activeClientId))

  // Seleção da aba ativa (no nível Contas o filtro não se aplica → null).
  const levelFilterIds = activeLevel === 'account' ? null : (levelFilters[activeLevel] ?? null)

  // Atualiza só a entrada da aba ativa e persiste. Aceita valor ou updater
  // funcional (mesma assinatura usada por toggle/selectAll/clearAll).
  const setLevelFilterIds = useCallback((updater) => {
    setLevelFilters(prev => {
      const current = prev[activeLevel] ?? null
      const nextVal = typeof updater === 'function' ? updater(current) : updater
      const next = { ...prev, [activeLevel]: nextVal }
      writeLevelFilters(activeClientId, next)
      return next
    })
  }, [activeLevel, activeClientId])

  // ESTADO E FUNÇÃO PARA O NOVO BOTÃO DE SINCRONIZAÇÃO
  const [isSyncing, setIsSyncing] = useState(false)

  // Fase 5 (B5): troca de cliente invalida drill-down e seleções locais.
  // IDs de ad_account/campaign/adGroup do cliente anterior não pertencem ao
  // novo cliente — manter selecionados causa filtros vazios e breadcrumbs
  // órfãos. O guard `activeClientId === null` evita disparo no mount inicial
  // enquanto AppContext ainda está resolvendo.
  useEffect(() => {
    if (activeClientId === null) return
    setSelectedAccounts([])
    setSelectedAccount(null)
    setSelectedCampaign(null)
    setSelectedGroup(null)
    setActiveLevel('account')
    // Recarrega os filtros de nível persistidos do novo cliente (IDs do
    // cliente anterior não valem). Cada cliente tem seu próprio estado por aba.
    setLevelFilters(readLevelFilters(activeClientId))
  }, [activeClientId])

  const handleForceSyncToday = async () => {
    setIsSyncing(true)
    toast('Iniciando coleta de dados de HOJE. Isso pode demorar um pouco...', 'info')
    
    try {
      await syncToday()
      
      toast('Dados de hoje atualizados!', 'success')
      
      // Rebusca os dados frescos do banco sem recarregar a página
      reloadAll()
      
    } catch (error) {
      console.error(error)
      toast('Erro ao atualizar dados de hoje.', 'error')
    } finally {
      setIsSyncing(false)
    }
  }

  const { 
    ACCOUNTS_ENRICHED, 
    CAMPAIGNS_ENRICHED, 
    AD_GROUPS_ENRICHED, 
    ADS_ENRICHED 
  } = useMemo(() => {
    if (isLoading || hasError) {
      return { ACCOUNTS_ENRICHED: [], CAMPAIGNS_ENRICHED: [], AD_GROUPS_ENRICHED: [], ADS_ENRICHED: [] }
    }

    const accsEnriched = accountsData.map(a => {
      const accCampaigns = campaignsData.filter(c => String(c.accountId) === String(a.id) && c.status !== 'removed')
      
      const cost = accCampaigns.reduce((s, c) => s + (c.metrics?.cost || 0), 0)
      const impressions = accCampaigns.reduce((s, c) => s + (c.metrics?.impressions || 0), 0)
      const clicks = accCampaigns.reduce((s, c) => s + (c.metrics?.clicks || 0), 0)

      const accCrmData = crmData.filter(s => 
         String(s.accountId) === String(a.id) // Compara UUID com UUID
      );

      const realVisits = accCrmData.reduce((sum, s) => sum + Number(s.visits || 0), 0)
      const realLeads = accCrmData.reduce((sum, s) => sum + Number(s.leads || 0), 0)
      const realWhatsappClicks = accCrmData.reduce((sum, s) => sum + Number(s.whatsappClicks || 0), 0)
      const realForms = accCrmData.reduce((sum, s) => sum + Number(s.forms || 0), 0)
      const realContacts = accCrmData.reduce((sum, s) => sum + Number(s.contacts || 0), 0)
      const realQualifiedLeads = accCrmData.reduce((sum, s) => sum + Number(s.qualifiedLeads || 0), 0)
      const realSales = accCrmData.reduce((sum, s) => sum + Number(s.sales || 0), 0)
      const realRevenue = accCrmData.reduce((sum, s) => sum + Number(s.revenue || 0), 0)

      const { status, type, ...cleanAccount } = a

      return {
        ...cleanAccount,
        cost,
        impressions,
        clicks,
        visits: realVisits,
        whatsappClicks: realWhatsappClicks,
        forms: realForms,
        contacts: realContacts,
        leads: realLeads,
        qualifiedLeads: realQualifiedLeads,
        sales: realSales,
        revenue: realRevenue,
        ticketMedio: realSales > 0 ? realRevenue / realSales : 0,
        roas: cost > 0 ? realRevenue / cost : 0,
        ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
        cpc: clicks > 0 ? cost / clicks : 0,
        cpl: realLeads > 0 ? cost / realLeads : 0, // CPL Real
        cac: realSales > 0 ? cost / realSales : 0, // CAC Real
        campaignCount: accCampaigns.length,
        activeCampaigns: accCampaigns.filter(c => c.status === 'active').length
      }
    })

    const campsEnriched = campaignsData.filter(c => c.status !== 'removed').map(c => {
      const account = accountsData.find(a => String(a.id) === String(c.accountId))
      const m = c.metrics || {}

      const campCrmData = crmData.filter(s => 
        String(s.accountId) === String(account?.id) &&
        String(s.campaignId) === String(c.id)
      );
      
      const realVisits = campCrmData.reduce((sum, s) => sum + Number(s.visits || 0), 0)
      const realLeads = campCrmData.reduce((sum, s) => sum + Number(s.leads || 0), 0)
      const realWhatsappClicks = campCrmData.reduce((sum, s) => sum + Number(s.whatsappClicks || 0), 0)
      const realForms = campCrmData.reduce((sum, s) => sum + Number(s.forms || 0), 0)
      const realContacts = campCrmData.reduce((sum, s) => sum + Number(s.contacts || 0), 0)
      const realQualifiedLeads = campCrmData.reduce((sum, s) => sum + Number(s.qualifiedLeads || 0), 0)
      const realSales = campCrmData.reduce((sum, s) => sum + Number(s.sales || 0), 0)
      const realRevenue = campCrmData.reduce((sum, s) => sum + Number(s.revenue || 0), 0)

      return {
        ...c,
        ...m, 
        accountName: account?.name || 'Unknown',
        account: account?.name?.split(' - ')[0] || 'Unknown',
        visits: realVisits,
        whatsappClicks: realWhatsappClicks,
        forms: realForms,
        contacts: realContacts,
        leads: realLeads,
        qualifiedLeads: realQualifiedLeads,
        sales: realSales,
        revenue: realRevenue,
        ticketMedio: realSales > 0 ? realRevenue / realSales : 0,
      }
    })

    const adGroupsEnriched = adGroupsData.map(g => {
      const account = accountsData.find(a => String(a.id) === String(g.accountId))
      const m = g.metrics || {}

      // CRUZAMENTO INTELIGENTE: Caça o ID ou Nome do Grupo em qualquer coluna UTM
      const groupCrmData = crmData.filter(s => {
        if (String(s.accountId) !== String(account?.id)) return false;
        
        const utmMedium = String(s.adGroupId || '');
        const utmContent = String(s.adId || '');
        const groupId = String(g.id);
        const groupName = String(g.name);

        return utmMedium.includes(groupId) || 
               utmContent.includes(groupId) ||
               (groupName && groupName !== 'Unknown' && (utmMedium.includes(groupName) || utmContent.includes(groupName)));
      })

      const realVisits = groupCrmData.reduce((sum, s) => sum + Number(s.visits || 0), 0)
      const realLeads = groupCrmData.reduce((sum, s) => sum + Number(s.leads || 0), 0)
      const realWhatsappClicks = groupCrmData.reduce((sum, s) => sum + Number(s.whatsappClicks || 0), 0)
      const realForms = groupCrmData.reduce((sum, s) => sum + Number(s.forms || 0), 0)
      const realContacts = groupCrmData.reduce((sum, s) => sum + Number(s.contacts || 0), 0)
      const realQualifiedLeads = groupCrmData.reduce((sum, s) => sum + Number(s.qualifiedLeads || 0), 0)
      const realSales = groupCrmData.reduce((sum, s) => sum + Number(s.sales || 0), 0)
      const realRevenue = groupCrmData.reduce((sum, s) => sum + Number(s.revenue || 0), 0)

      return {
        ...g,
        ...m,
        accountName: account?.name?.split(' - ')[0] || 'Unknown',
        visits: realVisits,
        whatsappClicks: realWhatsappClicks,
        forms: realForms,
        contacts: realContacts,
        leads: realLeads,
        qualifiedLeads: realQualifiedLeads,
        sales: realSales,
        revenue: realRevenue,
        ticketMedio: realSales > 0 ? realRevenue / realSales : 0,
      }
    })

    const adsEnriched = adsData.map(ad => {
      const account = accountsData.find(a => String(a.id) === String(ad.accountId))
      const m = ad.metrics || {}

      // CRUZAMENTO INTELIGENTE: Caça o ID do Anúncio (Creative) em qualquer coluna UTM
      const adCrmData = crmData.filter(s => {
        if (String(s.accountId) !== String(account?.id)) return false; 
        
        const utmMedium = String(s.adGroupId || '');
        const utmContent = String(s.adId || '');
        const adId = String(ad.id);

        return utmMedium.includes(adId) || utmContent.includes(adId);
      })

      const realVisits = adCrmData.reduce((sum, s) => sum + Number(s.visits || 0), 0)
      const realLeads = adCrmData.reduce((sum, s) => sum + Number(s.leads || 0), 0)
      const realWhatsappClicks = adCrmData.reduce((sum, s) => sum + Number(s.whatsappClicks || 0), 0)
      const realForms = adCrmData.reduce((sum, s) => sum + Number(s.forms || 0), 0)
      const realContacts = adCrmData.reduce((sum, s) => sum + Number(s.contacts || 0), 0)
      const realQualifiedLeads = adCrmData.reduce((sum, s) => sum + Number(s.qualifiedLeads || 0), 0)
      const realSales = adCrmData.reduce((sum, s) => sum + Number(s.sales || 0), 0)
      const realRevenue = adCrmData.reduce((sum, s) => sum + Number(s.revenue || 0), 0)

      return {
        ...ad,
        ...m,
        accountName: account?.name?.split(' - ')[0] || 'Unknown',
        visits: realVisits,
        whatsappClicks: realWhatsappClicks,
        forms: realForms,
        contacts: realContacts,
        leads: realLeads,
        qualifiedLeads: realQualifiedLeads,
        sales: realSales,
        revenue: realRevenue,
        ticketMedio: realSales > 0 ? realRevenue / realSales : 0,
      }
    })

    return { 
      ACCOUNTS_ENRICHED: accsEnriched, 
      CAMPAIGNS_ENRICHED: campsEnriched, 
      AD_GROUPS_ENRICHED: adGroupsEnriched, 
      ADS_ENRICHED: adsEnriched 
    }
  }, [accountsData, campaignsData, adGroupsData, adsData, crmData, isLoading, hasError])

  // `scopedData` = linhas do nível ativo já filtradas por drill-down, conta
  // (AccountFilter) e cliente (ClientSwitcher). É a base do filtro de nível.
  const scopedData = useMemo(() => {
    let data = []

    if (activeLevel === 'account') {
      data = ACCOUNTS_ENRICHED
    } else if (activeLevel === 'campaign') {
      data = selectedAccount
        ? CAMPAIGNS_ENRICHED.filter(c => String(c.accountId) === String(selectedAccount.id))
        : CAMPAIGNS_ENRICHED
    } else if (activeLevel === 'adgroup') {
      data = selectedCampaign
        ? AD_GROUPS_ENRICHED.filter(g => String(g.campaignId) === String(selectedCampaign.id))
        : AD_GROUPS_ENRICHED
    } else if (activeLevel === 'ad') {
      data = selectedGroup
        ? ADS_ENRICHED.filter(a => String(a.adGroupId) === String(selectedGroup.id))
        : ADS_ENRICHED
    }

    if (selectedAccounts.length > 0) {
      data = data.filter(item => {
        const itemAccId = String(item.accountId || item.id)
        const selectedStrings = selectedAccounts.map(String)
        return selectedStrings.includes(itemAccId)
      })
    }

    // Filtro "Todas as Contas" (ClientSwitcher): quando um cliente está
    // selecionado, exibe APENAS as linhas das contas desse cliente — mesma
    // lógica de ocultar linhas do AccountFilter (ao lado da data). Necessário
    // porque /accounts não filtra por client_id (retorna todas as contas),
    // enquanto as métricas vêm já escopadas — sem isso as demais contas
    // apareceriam zeradas em vez de ocultas.
    if (activeClientId) {
      const clientAccountIds = accountsData
        .filter(a => String(a.client_id) === String(activeClientId))
        .map(a => String(a.id))
      data = data.filter(item => {
        const itemAccId = String(item.accountId || item.id)
        return clientAccountIds.includes(itemAccId)
      })
    }

    return data
  }, [activeLevel, selectedAccount, selectedCampaign, selectedGroup, selectedAccounts, activeClientId, accountsData, ACCOUNTS_ENRICHED, CAMPAIGNS_ENRICHED, AD_GROUPS_ENRICHED, ADS_ENRICHED])

  // Itens disponíveis no filtro de nível, mapeados para a shape esperada pelo
  // CampaignFilter ({id, name, source, status}). source = nome da conta.
  const levelFilterItems = useMemo(
    () => scopedData.map(item => ({
      id: String(item.id),
      name: item.name || `#${item.id}`,
      source: item.accountName || item.account || '',
      status: item.status === 'paused' ? 'paused' : 'active',
    })),
    [scopedData]
  )

  // Callbacks do filtro de nível (espelham toggle/selectAll/clearAll do hook
  // global, mas operam sobre o estado local `levelFilterIds`).
  const lvlSelectAll = useCallback(() => setLevelFilterIds(null), [setLevelFilterIds])
  const lvlClearAll = useCallback(() => setLevelFilterIds([]), [setLevelFilterIds])
  const lvlToggle = useCallback((id) => {
    setLevelFilterIds(prev => {
      const base = prev ?? levelFilterItems.map(c => c.id)
      const next = base.includes(id) ? base.filter(x => x !== id) : [...base, id]
      // Array com tamanho total → normaliza para null ("todas").
      return next.length === levelFilterItems.length ? null : next
    })
  }, [levelFilterItems, setLevelFilterIds])

  // Wording por nível (singular do botão "Aplicar" reaproveitado).
  const LEVEL_FILTER_TEXTS = {
    campaign: { allLabel: 'Todas as campanhas', nounPlural: 'campanhas', placeholder: 'Buscar campanha...', empty: 'Nenhuma campanha encontrada', selectAll: 'Selecionar todas', none: 'Nenhuma campanha' },
    adgroup: { allLabel: 'Todos os grupos', nounPlural: 'grupos', placeholder: 'Buscar grupo...', empty: 'Nenhum grupo encontrado', selectAll: 'Selecionar todos', none: 'Nenhum grupo' },
    ad: { allLabel: 'Todos os anúncios', nounPlural: 'anúncios', placeholder: 'Buscar anúncio...', empty: 'Nenhum anúncio encontrado', selectAll: 'Selecionar todos', none: 'Nenhum anúncio' },
  }

  // Controlador no formato de retorno do useCampaignFilter() — alimenta o
  // CampaignFilter em modo controlado.
  const levelFilterController = useMemo(() => {
    const texts = LEVEL_FILTER_TEXTS[activeLevel] || LEVEL_FILTER_TEXTS.campaign
    const total = levelFilterItems.length
    const isAllSelected = levelFilterIds === null || levelFilterIds.length === total
    const effectiveIds = isAllSelected ? levelFilterItems.map(c => c.id) : levelFilterIds
    const count = isAllSelected ? total : levelFilterIds.length

    let label
    if (isAllSelected) {
      label = texts.allLabel
    } else if (count === 0) {
      label = texts.none
    } else if (count === 1) {
      label = levelFilterItems.find(c => c.id === levelFilterIds[0])?.name || `1 ${texts.nounPlural}`
    } else {
      label = `${count} ${texts.nounPlural}`
    }

    return {
      available: levelFilterItems,
      selectedIds: effectiveIds,
      isAllSelected,
      isFiltered: !isAllSelected,
      count,
      total,
      label,
      toggle: lvlToggle,
      selectAll: lvlSelectAll,
      clearAll: lvlClearAll,
    }
  }, [activeLevel, levelFilterItems, levelFilterIds, lvlToggle, lvlSelectAll, lvlClearAll])

  // `filteredData` = scopedData restringido pela seleção do filtro de nível.
  const filteredData = useMemo(() => {
    if (levelFilterIds === null) return scopedData
    const set = new Set(levelFilterIds.map(String))
    return scopedData.filter(item => set.has(String(item.id)))
  }, [scopedData, levelFilterIds])

  const handleAccountClick = useCallback((account) => {
    setSelectedAccount(account)
    setActiveLevel('campaign')
  }, [])

  const handleCampaignClick = useCallback((campaign) => {
    setSelectedCampaign(campaign)
    setActiveLevel('adgroup')
  }, [])

  const handleGroupClick = useCallback((group) => {
    setSelectedGroup(group)
    setActiveLevel('ad')
  }, [])

  const handleBreadcrumbNavigate = useCallback((item) => {
    if (item.id === 'accounts') {
      setActiveLevel('account')
      setSelectedAccount(null)
      setSelectedCampaign(null)
      setSelectedGroup(null)
    } else if (item.id === 'campaigns') {
      setActiveLevel('campaign')
      setSelectedCampaign(null)
      setSelectedGroup(null)
    } else if (item.id === 'adgroups') {
      setActiveLevel('adgroup')
      setSelectedGroup(null)
    }
  }, [])

  const handleLevelChange = useCallback((level) => {
    setActiveLevel(level)
    if (level === 'account') {
      setSelectedAccount(null)
      setSelectedCampaign(null)
      setSelectedGroup(null)
    }
  }, [])

  const breadcrumbItems = useMemo(() => {
    const items = [{ id: 'accounts', label: 'Contas', active: activeLevel === 'account' }]

    if (selectedAccount) {
      items.push({
        id: 'campaigns',
        label: selectedAccount.name?.split(' - ')[0] || 'Campanhas',
        active: activeLevel === 'campaign'
      })
    }

    if (selectedCampaign) {
      items.push({
        id: 'adgroups',
        label: selectedCampaign.name,
        active: activeLevel === 'adgroup'
      })
    }

    if (selectedGroup) {
      items.push({
        id: 'ads',
        label: selectedGroup.name,
        active: activeLevel === 'ad'
      })
    }

    return items
  }, [activeLevel, selectedAccount, selectedCampaign, selectedGroup])

  const levels = [
    { id: 'account', label: 'Contas' },
    { id: 'campaign', label: 'Campanhas', disabled: activeLevel !== 'account' && !selectedAccount },
    { id: 'adgroup', label: 'Grupos', disabled: activeLevel !== 'campaign' && activeLevel !== 'account' && !selectedCampaign },
    { id: 'ad', label: 'Anúncios', disabled: activeLevel === 'ad' && !selectedGroup },
  ]

  const handleDrillDown = useCallback((item) => {
    if (activeLevel === 'account') {
      handleAccountClick(item)
    } else if (activeLevel === 'campaign') {
      handleCampaignClick(item)
    } else if (activeLevel === 'adgroup') {
      handleGroupClick(item)
    }
  }, [activeLevel, handleAccountClick, handleCampaignClick, handleGroupClick])

  const renderPage = () => {
    const pageProps = {
      data: filteredData,
      level: activeLevel,
      onDrillDown: handleDrillDown,
    }

    // Card "Clicks WhatsApp" no nível Contas (todas as contas) usa o total
    // canônico do backend (= DRE/Dashboard), pois a soma das linhas por conta
    // não inclui cliques com gclid válido sem ad_account_id atribuído.
    const whatsappTotalOverride =
      activeLevel === 'account' &&
      selectedAccounts.length === 0 &&
      whatsTotalData?.total != null
        ? whatsTotalData.total
        : null

    switch (activePage) {
      case 'quantitative': return <QuantitativePage {...pageProps} whatsappTotalOverride={whatsappTotalOverride} />
      case 'rates': return <RatesPage {...pageProps} />
      case 'values': return <ValuesPage {...pageProps} />
      case 'goals': return (
        <PageState empty emptyText="Indisponível no momento" />
      )
      case 'projections': return <ProjectionsPage accounts={accountsData} />
      default: return <QuantitativePage {...pageProps} />
    }
  }

  return (
    <Layout
      title="Google Ads — Campanhas"
      breadcrumb={[{ label: 'Marketing' }, { label: 'Google Ads', to: '/google-ads' }, { label: 'Campanhas' }]}
      showPeriod
      topbarRight={<AccountSelect accounts={accountsData || []} selected={selectedAccounts} onChange={setSelectedAccounts} />}
    >
      <div className="ga-container">
        <div className="ga-filters-bar">
          <div className="ga-filters-left">
            <AccessGate require="org.sync">
              <button
                className={`btn btn-black btn-sm ${isSyncing ? 'loading' : ''}`}
                onClick={handleForceSyncToday}
                disabled={isSyncing}
              >
                <Icon n={isSyncing ? "clock" : "sync"} s={14} />
                {isSyncing ? 'Buscando...' : 'Puxar Dados de Hoje'}
              </button>
            </AccessGate>

            {/* Filtro dinâmico por nível (Campanhas/Grupos/Anúncios). Oculto no
                nível Contas — lá o filtro relevante é o seletor de conta da topbar. */}
            {activeLevel !== 'account' && (
              <CampaignFilter
                controller={levelFilterController}
                texts={LEVEL_FILTER_TEXTS[activeLevel]}
              />
            )}

          </div>
          <PageSelector activePage={activePage} onChange={setActivePage} />
        </div>

        <LevelSelector levels={levels} activeLevel={activeLevel} onChange={handleLevelChange} />

        {selectedAccount && <Breadcrumb items={breadcrumbItems} onNavigate={handleBreadcrumbNavigate} />}

        <div className="ga-content-area">
          <PageState loading={isLoading} error={hasError} loadingText="Carregando dados das contas..." onRetry={reloadAll}>
            {renderPage()}
          </PageState>
        </div>
      </div>
    </Layout>
  )
}
